import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { observer } from 'mobx-react'

import { Box, Button, CircularProgress, IconButton, Table, TableBody, TableCell, TableContainer, TableRow, Tooltip, Typography, useTheme } from '@mui/material'
import { exportMeetingAttendanceReport, formatDate, Icons, secondsToHMS, sortArrayOfObjectsByKey } from '../../../utils/utilities'
import { useOnlineSessionsStore } from '../../../store/onlinesessions.store'
import { getTransformedMeetingType } from '../../../utils/meeting-utilities'
import TryAgainMessageBlock from '../../common/TryAgainMessageBlock'
import { useUserStore } from '../../../store/user.store'
import CustomSwitch from '../../common/CustomSwitch'
import noDataImg from '../../../assets/no-data.svg'
import TableHeader from '../../common/TableHeader'
import CustomChip from '../../common/CustomChip'
import Progress from '../../common/Progress'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import api from '../../../service/api'
import ConfirmDialog from '../../common/ConfirmDialog'
import { useSettingsStore } from '../../../store/settings.store'
import { observable } from 'mobx'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'

const stickyCellShadow = {
    content: `''`,
    width: "4px",
    borderRadius: "2px 0 0 2px",
    background: `linear-gradient(to left, rgba(0, 0, 0, 0.15), transparent)`,
    position: "absolute",
    right: "100%",
    top: "0",
    bottom: "0"
}

const AttendanceTableRow = observer( ( { sessionAttendance = true, user, index } ) => {

    const [timeExtendConfirmWindowOpen, setTimeExtendConfirmWindowOpen] = useState( false )
    const [extendingTime, setExtendingTime] = useState( false )
    const [collapse, setCollapse] = useState()

    const { palette, border, layout } = useTheme()

    const OnlineSessionsStore = useOnlineSessionsStore()
    const SettingsStore = useSettingsStore()
    const UserStore = useUserStore()

    const classVideos = OnlineSessionsStore.getStreamRecordings?.filter( v => v.type !== "recording" )
    const quizParticipants = OnlineSessionsStore.getQuizParticipationAttendance
    const viewersList = OnlineSessionsStore.getVideosWatchAttendance
    const meeting = OnlineSessionsStore.getSessionMetadata
    const polls = OnlineSessionsStore.getPolls

    const videoWatchingThreshold = SettingsStore.getUseSettings?.["hybrid_class_video_watch_progress_threshold"] || 80

    const allVotes = polls.map( poll => ( poll.votes || [] ).flat() ).flat()

    const userVotes = allVotes.filter( vote => vote.user_id === user.user_id )

    const votePercentage = polls.length ? Math.round( ( userVotes.length / polls.length ) * 100 ) : 0

    const pollSubmissionsDetails = {
        votePercentage,
        votes: userVotes.length,
        totalPolls: polls.length
    }

    const didUserWatchVideos = viewersList?.find( u => u.user_id === user.user_id )
    const videoWatchDetails = sessionAttendance ? didUserWatchVideos?.videosViewDetails || [] : user.videosViewDetails

    const canExtendTime = classVideos.some( video => {

        const endDate = video.endDate

        return dayjs( meeting.starts_at ).isSame( dayjs(), 'day' ) &&
            dayjs( endDate ).isSame( dayjs(), 'day' ) &&
            !dayjs( endDate ).isSame( dayjs().set( 'hour', 17 ).set( 'minute', 20 ), 'minute' ) &&
            dayjs().isBefore( dayjs().set( 'hour', 17 ).set( 'minute', 20 ) )
    } )


    const extendUserAccessTime = async () => {
        try {
            let today520PM = dayjs().set( 'hour', 17 ).set( 'minute', 20 )
            setExtendingTime( true )

            const payload = {
                access_ends_at: today520PM,
                target: "student-access",
                overrides: [user.user_id]
            }

            const updates = []
            const overrideEligibleClassVideos = classVideos.filter( video => {

                const endDate = video.access_overrides[user.user_id]?.access_ends_at || video.endDate

                return dayjs( meeting.starts_at ).isSame( dayjs(), 'day' ) &&
                    dayjs( endDate ).isSame( dayjs(), 'day' ) &&
                    !dayjs( endDate ).isSame( dayjs().set( 'hour', 17 ).set( 'minute', 20 ), 'minute' ) &&
                    dayjs().isBefore( dayjs().set( 'hour', 17 ).set( 'minute', 20 ) )
            } )

            for ( const video of overrideEligibleClassVideos ) {
                updates.push( api.onlineMeetings.extendHybridClassVideoTime( meeting.id, video.id, payload ) )
            }

            await Promise.all( updates )

            await OnlineSessionsStore.fetchOnlineSessionRecordings( meeting.id )
            setTimeExtendConfirmWindowOpen( false )
        } catch ( err ) {
            toast( err?.response?.data?.message || "Something went wrong while extending user access time!" )
        }
        setExtendingTime( false )
    }

    return (
        <React.Fragment key={user.user_id}>

            <ConfirmDialog status={timeExtendConfirmWindowOpen} waiting={extendingTime} message={`Would you like to extend access time for ${user.name} upto 5:20PM for the video content?`} actionName='Yes, Extend access time' themeColor="primary" cancelAction={() => setTimeExtendConfirmWindowOpen( false )} confirmAction={extendUserAccessTime} />

            <TableRow onClick={() => setCollapse( prev => !prev )} sx={{ bgcolor: palette.common.bg, borderBottom: collapse ? `1px solid ${palette.primary.main} !important` : "", cursor: "pointer", ":hover": { bgcolor: palette.rowHover } }} >
                <TableCell sx={{ padding: "5px 20px", borderBottom: border[1] }}>
                    {index}
                </TableCell>
                <TableCell sx={{ padding: "5px 20px", borderBottom: border[1] }}>
                    {user?.user_id}
                </TableCell>
                <TableCell sx={{ padding: "5px 20px", borderBottom: border[1] }}>
                    {user.name}
                </TableCell>

                {meeting.meeting_type === "hybrid-class" && <TableCell sx={{ padding: "5px 20px", borderBottom: border[1] }}>
                    <Box display="flex" gap="5px" alignItems="center" flexWrap="wrap">
                        {sessionAttendance && <CustomChip startIcon={sessionAttendance ? Icons.default.CheckCircleIcon : Icons.default.BlockIcon} value="Stream" />}
                        <CustomChip startIcon={didUserWatchVideos ? Icons.default.CheckCircleIcon : Icons.default.BlockIcon} value="Video" />
                        <CustomChip startIcon={quizParticipants.includes( user.user_id ) ? Icons.default.CheckCircleIcon : Icons.default.BlockIcon} value="Quiz" />
                    </Box>
                </TableCell>}
                {meeting.meeting_type === "hybrid-class" && <TableCell sx={{ padding: "5px 20px", borderBottom: border[1] }}>
                    {Math.min( 100, ( didUserWatchVideos?.average || 0 ) )}%
                </TableCell>}

                {sessionAttendance && meeting.meeting_type !== "hybrid-class" && <TableCell sx={{ padding: "5px 20px", borderBottom: border[1] }} align="center" >
                    {polls.length > 0 ? `${pollSubmissionsDetails.votes}/${pollSubmissionsDetails.totalPolls} (${pollSubmissionsDetails.votePercentage}%)}` : "No polls"}
                </TableCell>}
                {!sessionAttendance && meeting.meeting_type !== "hybrid-class" && <TableCell sx={{ padding: "5px 20px", borderBottom: border[1] }} align="center" >
                    NA
                </TableCell>}
                {meeting.meeting_type !== "hybrid-class" && <TableCell sx={{ padding: "5px 20px", borderBottom: border[1] }} align="center" >
                    {sessionAttendance ? `${secondsToHMS( user.total_time / 1000 )} (${user.present_percentage}%)` : "00:00 (0%)"}
                </TableCell>}
                <TableCell
                    sx={{
                        padding: "5px 20px",
                        borderBottom: border[1],
                        position: "sticky",
                        right: "-1px",
                        background: "inherit",
                        "&::before": stickyCellShadow
                    }}
                    align="center"
                >
                    <CustomChip variant='filled' color={( user.present || didUserWatchVideos?.average >= videoWatchingThreshold ) ? "success" : "error"} value={( user.present || didUserWatchVideos?.average >= videoWatchingThreshold ) ? "PRESENT" : "ABSENT"} />

                    {/* {UserStore.getUser.user_id === meeting.created_by && meeting.meeting_type === "hybrid-class" && !( user.present || didUserWatchVideos?.average >= videoWatchingThreshold ) && canExtendTime && <Tooltip title="Manage student access to video content">
                        <Box component="span" >
                            <IconButton disabled={extendingTime} onClick={() => setTimeExtendConfirmWindowOpen( true )} >
                                {extendingTime ? <CircularProgress size={20} /> : Icons.default.Clock}
                            </IconButton>
                        </Box>
                    </Tooltip>} */}
                </TableCell>
            </TableRow>
            {collapse && <TableRow>
                <TableCell sx={{ padding: "0 0 5px 0" }} colSpan={10} >
                    <Box bgcolor={palette.contentBg} width="100%" borderRadius="0 0 5px 5px" padding="10px">
                        {user.sessions && <Box display="flex" gap="10px" alignItems="center" flexWrap="wrap" >
                            {user.sessions.length > 0 && user.sessions.map( ( session, index ) => (
                                <Box borderRadius={layout.borderRadius} padding="5px" border={border[1]} key={index}>
                                    <Typography fontWeight={500} fontSize="10px" >{formatDate( session.joined_at, { format: "hh:mm A" } )} - {formatDate( session.left_at, { format: "hh:mm A" } )}</Typography>
                                    <Typography fontSize="10px" >{secondsToHMS( session.duration / 1000, false )}</Typography>
                                </Box>
                            ) )}
                        </Box>}
                        {videoWatchDetails.length > 0 && <Box display="flex" gap="5px" alignItems="center" marginTop="10px">
                            {quizParticipants.includes( user.user_id ) && <CustomChip value="Quiz attempted" endIcon={Icons.default.CheckCircleIcon} startIcon={Icons.default.QuizIcon} key={"quiz"} />}
                            {videoWatchDetails.map( ( video, index ) => (
                                <CustomChip tooltipTitle={`Completed: ${video.progress}%, Started At: ${dayjs( video.started_at ).format( "LLL" )}, Last Updated At: ${dayjs( video.updated_at ).format( "LLL" )}`} chipStyles={{ paddingRight: "0" }} value={video.title} startIcon={Icons.default.Movie} key={index} endIcon={<Progress secondaryColor="GrayText" size={20} progress={video.progress} trackWidth={2} indicatorWidth={2} color={palette.secondary.main} />} />
                            ) )}
                        </Box>}
                    </Box>
                </TableCell>
            </TableRow>}
        </React.Fragment>
    )
} )

const OnlineSessionAttendanceDetails = observer( ( { meeting } ) => {

    const [showOnlyPresent, setShowOnlyPresent] = useState( false )
    const [hostsDuration, setHostsDuration] = useState( true )

    const { palette, border, layout } = useTheme()
    const { meeting_id } = useParams()

    const OnlineSessionsStore = useOnlineSessionsStore()
    const SettingsStore = useSettingsStore()
    const UserStore = useUserStore()

    const videoWatchAttendance = OnlineSessionsStore.getVideosWatchAttendance
    const creatorAttendanceData = OnlineSessionsStore.getCreatorAttendance
    const { initialLoad: loading, refreshing } = OnlineSessionsStore.getAttendanceLoadingStatus
    const error = OnlineSessionsStore.getErrors.onlineSessionAttendance
    const streamAttendance = OnlineSessionsStore.getStreamAttendance

    const videoWatchingThreshold = SettingsStore.getUseSettings?.["hybrid_class_video_watch_progress_threshold"] || 80
    const streamThreshold = SettingsStore.getUseSettings?.["online_class_attendance_threshold"] || 50

    const videoWatchPresentUserIds = useMemo( () => {
        return videoWatchAttendance.filter( viewer => viewer.average >= videoWatchingThreshold ).map( viewer => viewer.user_id )
    }, [videoWatchAttendance, videoWatchingThreshold] )


    const attendanceColumns = useMemo( () => {

        const allColumns = [
            { name: "SL. NO.", minWidth: "90px", padding: "10px 20px", hidden: false },
            { name: "User ID", minWidth: "100px", padding: "10px 20px", hidden: false },
            { name: "Name", minWidth: "250px", padding: "10px 20px", hidden: false },
            { name: "Activities", minWidth: "250px", padding: "10px 20px", hidden: meeting.meeting_type !== "hybrid-class" },
            { name: "Video Watch Status", minWidth: "250px", padding: "10px 20px", hidden: meeting.meeting_type !== "hybrid-class" },
            { name: "Poll Submissions", minWidth: "250px", align: "center", padding: "10px 20px", hidden: meeting.meeting_type === "hybrid-class" },
            { name: "Attendance", minWidth: "250px", align: "center", padding: "10px 20px", hidden: meeting.meeting_type === "hybrid-class" },
            {
                name: "Present Status", minWidth: "160px", align: "center", padding: "10px 20px", hidden: false, styles: {
                    position: "sticky", right: "-1px", bgcolor: palette.common.bg,
                    "&::before": stickyCellShadow
                }
            },
        ]
        const columns = allColumns.filter( col => !col.hidden )
        return columns

    }, [meeting, palette] )

    const fetchMeetingAttendance = useCallback( async () => {
        OnlineSessionsStore.fetchOnlineSessionAttendance( meeting_id )
    }, [meeting_id, OnlineSessionsStore] )

    const fetchSessionPolls = useCallback( async () => {
        OnlineSessionsStore.fetchOnlineSessionPolls( meeting_id )
    }, [meeting_id, OnlineSessionsStore] )

    const calculatedAttendance = useMemo( () => {

        const calculatedAttendance = [...streamAttendance]

        const mergeSessions = ( sessions ) => {
            if ( !sessions?.length ) return []

            const sorted = [...sessions].sort( ( a, b ) => new Date( a.joined_at ) - new Date( b.joined_at ) )
            const merged = []
            let current = { ...sorted[0] }

            for ( let i = 1; i < sorted.length; i++ ) {
                const next = sorted[i]

                const currentEnd = new Date( current.left_at )
                const nextStart = new Date( next.joined_at )

                if ( nextStart <= currentEnd ) {
                    // merge if overlapping or touching
                    const nextEnd = new Date( next.left_at )
                    current.left_at = nextEnd > currentEnd ? next.left_at : current.left_at
                    current.duration = new Date( current.left_at ) - new Date( current.joined_at )
                } else {
                    merged.push( current )
                    current = { ...next }
                }
            }

            merged.push( current )
            return merged
        }

        const creatorSessions = mergeSessions( [...( creatorAttendanceData?.sessions || [] )].filter( ( { left_at, joined_at } ) => new Date( left_at ) > new Date( creatorAttendanceData?.meetingTime?.starts_at ) && new Date( joined_at ) < new Date( creatorAttendanceData?.meetingTime?.ends_at ) ) )

        // host's session boundaries
        let earliestSession = hostsDuration ? creatorSessions?.[0] || { joined_at: creatorAttendanceData?.meetingTime?.starts_at, left_at: creatorAttendanceData?.meetingTime?.ends_at } : { joined_at: creatorAttendanceData?.meetingTime?.starts_at, left_at: creatorAttendanceData?.meetingTime?.ends_at }
        let latestSession = hostsDuration ? creatorSessions?.[creatorSessions.length - 1] || { joined_at: creatorAttendanceData?.meetingTime?.starts_at, left_at: creatorAttendanceData?.meetingTime?.ends_at } : { joined_at: creatorAttendanceData?.meetingTime?.starts_at, left_at: creatorAttendanceData?.meetingTime?.ends_at }

        latestSession.left_at = new Date( Math.min( new Date( creatorAttendanceData?.meetingTime?.ends_at ), new Date( latestSession?.left_at ) ) )
        earliestSession.joined_at = new Date( Math.max( new Date( creatorAttendanceData?.meetingTime?.starts_at ), new Date( earliestSession?.joined_at ) ) )

        if ( earliestSession ) {
            earliestSession.joined_at = new Date( earliestSession.joined_at )
            earliestSession.left_at = new Date( earliestSession.left_at )
        }
        if ( latestSession ) {
            latestSession.joined_at = new Date( latestSession.joined_at )
            latestSession.left_at = new Date( latestSession.left_at )
        }


        // decide totalDuration
        let totalDuration = latestSession.left_at - earliestSession.joined_at
        if ( creatorSessions && creatorSessions.length > 0 )
            totalDuration = creatorSessions.reduce(
                ( acc, { duration } ) => acc + ( duration ),
                0
            )

        totalDuration = totalDuration || 10

        for ( const attendanceDetails of calculatedAttendance ) {
            let adjustedTotalTime = attendanceDetails.total_time

            if ( earliestSession && latestSession ) {

                // apply boundary trimming only if hostsDuration = true
                const adjustedSessions = []
                for ( const [idx, session] of mergeSessions( attendanceDetails.sessions || [] )?.entries() ) {
                    let { joined_at, left_at } = session
                    joined_at = new Date( joined_at )
                    left_at = new Date( left_at )

                    if ( left_at <= earliestSession.joined_at || joined_at >= latestSession.left_at ) {
                        continue
                    }
                    // Trim first session if before host's first joined_at
                    if ( idx === 0 && joined_at < earliestSession.joined_at ) {
                        joined_at = earliestSession.joined_at
                    }

                    // Trim last session if after host's last left_at
                    if ( idx === attendanceDetails.sessions.length - 1 && left_at > new Date( latestSession.left_at ) ) {
                        left_at = new Date( latestSession.left_at )
                    }

                    if ( left_at <= joined_at )
                        continue

                    adjustedSessions.push( { joined_at, left_at } )
                }

                adjustedTotalTime = adjustedSessions.reduce(
                    ( acc, { joined_at, left_at } ) => acc + ( left_at - joined_at ),
                    0
                )
            }

            attendanceDetails.total_time = adjustedTotalTime

            attendanceDetails.present = adjustedTotalTime >= ( totalDuration * ( streamThreshold / 100 ) )
            attendanceDetails.present_percentage = Math.min(
                Math.round( ( adjustedTotalTime * 100 ) / totalDuration ),
                100
            )
            console.log( attendanceDetails.name, secondsToHMS( adjustedTotalTime / 1000 ), secondsToHMS( totalDuration / 1000 ) )

            // console.log( attendanceDetails )

            attendanceDetails.threshold_percent = streamThreshold
            attendanceDetails.threshold_in_ms = totalDuration * ( streamThreshold / 100 )
        }

        return observable( sortArrayOfObjectsByKey( calculatedAttendance, 'total_time', -1, 'number' ) )
    }, [streamThreshold, hostsDuration, creatorAttendanceData, streamAttendance] )


    useEffect( () => {
        fetchMeetingAttendance()
        fetchSessionPolls()
    }, [fetchMeetingAttendance, fetchSessionPolls] )

    const presentCount = useMemo( () => {
        return new Set( [...calculatedAttendance.filter( u => u.present ).map( u => u.user_id ), ...videoWatchAttendance.filter( u => u.average > videoWatchingThreshold ).map( v => v.user_id )] ).size
    }, [calculatedAttendance, videoWatchAttendance, videoWatchingThreshold] )


    return (
        <Box flexGrow={1} display="flex" gap={layout.cardsGap} flexDirection="column" padding="10px">
            {!error && !loading && ( calculatedAttendance.length > 0 || videoWatchAttendance.length > 0 ) && <Box flexWrap="wrap" display="flex" justifyContent="space-between" gap="20px">
                <Box flexWrap="wrap" display="flex" gap={layout.cardsGap}>

                    {meeting.meeting_type === 'hybrid-class' && ["ADMIN", "SUBADMIN"].includes( UserStore.getUser.user_role ) && <Box display="flex" gap="10px" alignItems="center">
                        <CustomChip startIcon={Icons.default.Movie} value={`Video Watch Threshold: ${videoWatchingThreshold}%`} />
                    </Box>}

                    {String( meeting.created_by ) === String( UserStore.getUser.user_id ) && meeting.meeting_type !== 'hybrid-class' && <Box flexGrow={{ md: "initial", xs: "1" }} justifyContent="space-between" display="flex" gap="10px" alignItems="center" borderRadius={layout.borderRadius} >
                        <Box >
                            <Typography fontSize="14px">
                                Consider {String( meeting.created_by ) === String( UserStore.getUser.user_id ) ? "my" : "host"} time as duration. ({secondsToHMS( ( creatorAttendanceData?.total_time || 0 ) / 1000, false )})
                            </Typography>
                        </Box>
                        {String( meeting.created_by ) === String( UserStore.getUser.user_id ) && <CustomSwitch size="small" checked={hostsDuration} onChange={e => setHostsDuration( e.target.checked )} />}
                        <Typography color="GrayText"> | </Typography>
                    </Box>}

                    <Box flexGrow={{ md: "initial", xs: "1" }} justifyContent="space-between" display="flex" gap="10px" alignItems="center"  >
                        <Box >
                            <Typography fontSize="14px">
                                Show only attendance marked as present. ({presentCount})
                            </Typography>
                        </Box>
                        <CustomSwitch size="small" checked={showOnlyPresent} onChange={e => setShowOnlyPresent( e.target.checked )} />
                    </Box>
                </Box>
                <Box justifyContent="space-between" display="flex" gap="10px" alignItems="center">
                    <Button onClick={() => exportMeetingAttendanceReport( meeting, calculatedAttendance, videoWatchAttendance )} variant='outlined' startIcon={Icons.default.UploadFileIcon} color='secondary'>
                        Export Attendance
                    </Button>
                </Box>
            </Box>}

            {!error && !loading && String( meeting.created_by ) !== String( UserStore.getUser.user_id ) && <Box bgcolor={palette.contentBg} padding="10px" borderRadius={layout.borderRadius} order={border[1]}>
                <Typography variant='subtitle2'>Attendance is calculated based on the {meeting.meeting_type?.includes( "class" ) ? `${meeting.meeting_type}'` : `${meeting.meeting_type}'s`} host time.</Typography>
                <Box display="flex" marginTop={layout.cardsGap} flexWrap="wrap" alignItems="center" gap="5px">
                    {creatorAttendanceData?.sessions?.map( ( session, index ) => {

                        let differentJoinedAtForAttendanceConsideration = false
                        let differentLeftAtForAttendanceConsideration = false
                        if ( new Date( session.joined_at ) < new Date( meeting.starts_at ) )
                            differentJoinedAtForAttendanceConsideration = true
                        if ( new Date( session.left_at ) > new Date( meeting.ends_at ) )
                            differentLeftAtForAttendanceConsideration = true

                        return (
                            <Box borderRadius={layout.borderRadius} bgcolor={palette.background.paper} padding="5px" border={border[1]} key={index}>
                                <Typography lineHeight={1}>
                                    <Typography component="span" title={differentJoinedAtForAttendanceConsideration ? "Started Early" : ""} fontWeight={500} fontSize="10px" color={differentJoinedAtForAttendanceConsideration ? "textSecondary" : "textPrimary"} >{formatDate( session.joined_at, { format: "hh:mm A" } )}</Typography>
                                    <Typography component="span" fontWeight={500} fontSize="10px" > - </Typography>
                                    <Typography component="span" title={differentJoinedAtForAttendanceConsideration ? "Ended late" : ""} fontWeight={500} fontSize="10px" color={differentLeftAtForAttendanceConsideration ? "textSecondary" : "textPrimary"} > {formatDate( session.left_at, { format: "hh:mm A" } )}</Typography>
                                </Typography>
                                <Typography fontSize="10px" >{secondsToHMS( session.duration / 1000, false )}</Typography>
                            </Box>
                        )
                    } )}
                </Box>
            </Box>}

            {!error && !loading && ( calculatedAttendance.length > 0 || videoWatchAttendance.length > 0 ) && <Box display="flex" flexDirection="column" overflow="auto" gap="10px" flexGrow={1} alignItems="center" flexWrap='wrap'>
                {(
                    calculatedAttendance.filter( user => showOnlyPresent ? user.present_percentage > streamThreshold : true ).length > 0 ||
                    videoWatchAttendance.filter( user => showOnlyPresent ? user.average > videoWatchingThreshold : true ).length > 0
                ) &&
                    <TableContainer className='custom-scrollbar' sx={{ maxHeight: "350px", border: border[1], borderRadius: layout.borderRadius, overflow: "auto", flexGrow: 1 }}>
                        <Table stickyHeader>
                            <TableHeader columns={attendanceColumns} />
                            <TableBody>
                                {calculatedAttendance.filter( user => showOnlyPresent ? user.present_percentage > streamThreshold || videoWatchPresentUserIds.includes( user.user_id ) : true ).map( ( user, index ) => {

                                    return (
                                        <AttendanceTableRow index={index + 1} key={user.user_id} user={user} />
                                    )
                                } )}

                                {videoWatchAttendance.filter( user => showOnlyPresent ? user.average > videoWatchingThreshold : true ).map( ( viewer, index ) => {

                                    const didUserStreamClass = calculatedAttendance.find( u => u.user_id === viewer.user_id )

                                    if ( didUserStreamClass )
                                        return null

                                    return (
                                        <AttendanceTableRow sessionAttendance={false} index={calculatedAttendance.length + index + 1} key={viewer.user_id} user={viewer} />
                                    )
                                } )}
                            </TableBody>
                        </Table>
                    </TableContainer>}

                {
                    calculatedAttendance.filter( user => showOnlyPresent ? user.present_percentage > streamThreshold : true ).length === 0 &&
                    videoWatchAttendance.filter( user => showOnlyPresent ? user.average > videoWatchingThreshold : true ).length === 0 &&

                    <Box bgcolor={palette.contentBg} width="100%" padding="20px" display="flex" flexDirection="column" alignItems="center" gap="10px" borderRadius={layout.borderRadius} >
                        <Box component="img" width="200px" src={noDataImg} />
                        <Typography variant='subtitle2' component="p" textAlign="center">No users met the attendance threshold requirement. Remove the ‘Show Only Present’ filter to view all users.</Typography>
                    </Box>}

            </Box>}

            {!error && !loading && calculatedAttendance.length === 0 && videoWatchAttendance.length === 0 && <Box bgcolor={palette.contentBg} width="100%" padding="20px" display="flex" flexDirection="column" alignItems="center" gap="10px" borderRadius={layout.borderRadius} >
                <Box component="img" width="200px" src={noDataImg} />
                <Typography variant='subtitle2' component="p" textAlign="center">Attendance data unavailable! Please let the {getTransformedMeetingType( meeting?.meeting_type, { forDisplay: true } )} complete to access attedance data.</Typography>
            </Box>}

            {loading && <DataLoadingSpinner waitingMessage="Loading session attendance please wait..." />}

            {error && <TryAgainMessageBlock parent='component' getData={fetchMeetingAttendance} loading={refreshing} code={error?.code} err={error?.message} />}
        </Box>
    )
}
)
export default OnlineSessionAttendanceDetails
